import React, { useState, useEffect } from "react";
import { useOutletContext, Navigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { FileText, Download, Calendar, Filter, ShoppingBag, Search, Loader2, ChevronLeft, ChevronRight, Copy, MessageCircle, Mail, ChevronDown, User, Package } from "lucide-react";
import api from "../services/api";
import { Input } from "../components/ui/Input";
import { toast } from "sonner";
import { openWhatsAppChat, decodeHtmlEntities } from "../utils";
import { AsyncSelect } from "../components/AsyncSelect";
import { userService } from "../services/users";

const ALLOWED_EMAILS_REPORTS = [
  'contato@foradocloset.com.br',
  'ronald@ronald.com',
  'ariadne.foradocloset@gmail.com'
];

export function RelatoriosPage() {
  const { user } = useOutletContext<{ user: any }>();

  if (!user || !ALLOWED_EMAILS_REPORTS.includes(user.email)) {
    return <Navigate to="/produtos" replace />;
  }

  const [activeReport, setActiveReport] = useState("abandoned");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Filtros
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [includeVisitors, setIncludeVisitors] = useState(false);
  const [sortBy, setSortBy] = useState("count");

  const [expandedStates, setExpandedStates] = useState<string[]>([]);
  const [expandedCities, setExpandedCities] = useState<string[]>([]);
  const [admins, setAdmins] = useState<any[]>([]);
  const [selectedAdmin, setSelectedAdmin] = useState("");
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalNetRevenue, setTotalNetRevenue] = useState(0);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [expandedCategories, setExpandedCategories] = useState<number[]>([]);

  const toggleCategoryAccordion = (catId: number) => {
    setExpandedCategories(prev =>
      prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]
    );
  };

  // Filtros Relatório de Vendas
  const [salesStatus, setSalesStatus] = useState("all");
  const [salesView, setSalesView] = useState("detailed");
  const [selectedSupplierId, setSelectedSupplierId] = useState("");
  const [selectedSupplierLabel, setSelectedSupplierLabel] = useState("");

  const toggleState = (uf: string) => {
    setExpandedStates(prev =>
      prev.includes(uf) ? prev.filter(s => s !== uf) : [...prev, uf]
    );
  };

  const toggleCity = (cityId: string) => {
    setExpandedCities(prev =>
      prev.includes(cityId) ? prev.filter(c => c !== cityId) : [...prev, cityId]
    );
  };

  const reports = [
    { id: "abandoned", name: "Interessados / Sacolas", icon: ShoppingBag, endpoint: '/api/admin/reports/abandoned-carts' },
    { id: "category_pieces", name: "Peças por Categoria", icon: Package, endpoint: '/api/admin/reports/category-pieces' },
    { id: "salesman", name: "Vendas por Vendedora", icon: User, endpoint: '/api/admin/reports/sales-by-salesman' },
    { id: "customers", name: "Ranking de Clientes", icon: Filter, endpoint: '/api/admin/reports/customers' },
    { id: "suppliers", name: "Ranking de Desapego", icon: FileText, endpoint: '/api/admin/reports/suppliers-ranking' },
    { id: "states", name: "Vendas por Estado", icon: Calendar, endpoint: '/api/admin/reports/sales-by-state' },
    { id: "sales", name: "Relatório de Vendas", icon: FileText, endpoint: '/api/admin/reports/sales' },
  ];

  useEffect(() => {
    // Carrega vendedoras
    api.get('/api/admin?per_page=-1').then(res => {
      setAdmins(res.data.data.data || res.data.data || []);
    });

    // Define data padrão: hoje e 7 dias atrás
    const today = new Date().toISOString().split('T')[0];
    const lastWeek = new Date();
    lastWeek.setDate(lastWeek.getDate() - 7);
    const lastWeekStr = lastWeek.toISOString().split('T')[0];

    setStartDate(lastWeekStr);
    setEndDate(today);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    fetchData(1);
  }, [activeReport, startDate, endDate, includeVisitors, sortBy, searchTerm, selectedAdmin, salesStatus, salesView, selectedSupplierId]);

  const fetchData = async (page = currentPage) => {
    setLoading(true);
    try {
      const report = reports.find(r => r.id === activeReport);
      if (report) {
        const params: any = {
          page,
          start_date: startDate ? `${startDate} 00:00:00` : undefined,
          end_date: endDate ? `${endDate} 23:59:59` : undefined,
          include_visitors: includeVisitors,
          sort_by: sortBy,
          search: searchTerm || undefined,
          admin_id: (activeReport === 'salesman' || activeReport === 'sales') ? selectedAdmin : undefined
        };

        if (activeReport === 'sales') {
          params.status = salesStatus;
          params.view = salesView;
          params.user_id = selectedSupplierId || undefined;
        }

        const response = await api.get(report.endpoint, { params });

        setTotalRevenue(response.data.total_revenue || 0);
        setTotalNetRevenue(response.data.total_net_revenue || 0);
        setTotalAvailable(response.data.total_available || 0);
        const resData = response.data.data;
        if (resData.data) {
          setData(resData.data);
          setCurrentPage(resData.current_page);
          setLastPage(resData.last_page);
          setTotalItems(resData.total);
        } else {
          setData(resData);
          setTotalItems(resData.length);
          setLastPage(1);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar relatórios:", error);
      toast.error("Erro ao carregar dados do relatório");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    if (!text || text === 'N/A') return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado!`);
    } catch (err) {
      // Fallback para navegadores antigos ou contextos não seguros
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success(`${label} copiado!`);
      } catch (err) {
        toast.error(`Erro ao copiar ${label.toLowerCase()}`);
      }
      document.body.removeChild(textArea);
    }
  };

  const openWhatsApp = (phone: string) => {
    openWhatsAppChat(phone);
  };

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setIncludeVisitors(false);
    setSortBy("count");
    setSearchTerm("");
    setSelectedAdmin("");
    setSalesStatus("all");
    setSalesView("detailed");
    setSelectedSupplierId("");
    setSelectedSupplierLabel("");
    setTotalRevenue(0);
    setTotalNetRevenue(0);
    setTotalAvailable(0);
    setCurrentPage(1);
  };

  const exportToCSV = async () => {
    setLoading(true);
    try {
      const report = reports.find(r => r.id === activeReport);
      if (!report) return;

      if (activeReport === 'sales') {
        const response = await api.get('/api/admin/reports/sales/export', {
          params: {
            start_date: startDate ? `${startDate} 00:00:00` : undefined,
            end_date: endDate ? `${endDate} 23:59:59` : undefined,
            status: salesStatus,
            view: salesView,
            user_id: selectedSupplierId || undefined,
            admin_id: selectedAdmin || undefined
          },
          responseType: 'blob'
        });

        const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        const filename = `relatorio-vendas-${new Date().toISOString().split('T')[0]}.csv`;
        link.setAttribute("download", filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Relatório de vendas exportado com sucesso!");
        return;
      }

      const response = await api.get(report.endpoint, {
        params: {
          paginate: 'false',
          start_date: startDate ? `${startDate} 00:00:00` : undefined,
          end_date: endDate ? `${endDate} 23:59:59` : undefined,
          include_visitors: includeVisitors,
          sort_by: sortBy,
          search: searchTerm || undefined,
          admin_id: (activeReport === 'salesman' || activeReport === 'sales') ? selectedAdmin || undefined : undefined
        }
      });

      const allData = response.data.data;
      if (!allData || allData.length === 0) {
        toast.error("Não há dados para exportar");
        return;
      }

      if (activeReport === 'category_pieces') {
        const csvRows: string[] = [];
        csvRows.push(['Categoria', 'Subcategoria', 'Peças em Estoque'].join(','));

        allData.forEach((row: any) => {
          csvRows.push([
            JSON.stringify(row.category_name),
            '""',
            row.total_available
          ].join(','));

          if (row.subcategories && row.subcategories.length > 0) {
            row.subcategories.forEach((sub: any) => {
              csvRows.push([
                JSON.stringify(row.category_name),
                JSON.stringify(sub.category_name),
                sub.total_available
              ].join(','));
            });
          }
        });

        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `relatorio-pecas-categoria-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Relatório de peças por categoria exportado!");
        return;
      }

      const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
      };

      const headerMap: Record<string, string> = {
        'order_id': 'Pedido',
        'product_name': 'Produto',
        'product_sku': 'SKU',
        'price': 'Valor Bruto',
        'net_price': 'Valor Líquido',
        'sold_at': 'Data da Venda',
        'customer': 'Cliente',
        'gateway': 'Forma de Pagamento',
        'total_spent': 'Total Gasto (Bruto)',
        'total_net_spent': 'Total Gasto (Líquido)',
        'total_revenue': 'Valor Gerado (Bruto)',
        'total_net_revenue': 'Valor Gerado (Líquido)',
        'category_name': 'Categoria',
        'total_registered': 'Peças Cadastradas',
        'total_sold': 'Peças Vendidas',
        'total_available': 'Peças Disponíveis'
      };

      const rawHeaders = Object.keys(allData[0]);
      const headers = rawHeaders.map(h => headerMap[h] || h);

      const csvRows = [
        headers.join(','),
        ...allData.map((row: any) =>
          rawHeaders.map(fieldName => {
            let value = row[fieldName];
            if (typeof value === 'number' && (fieldName.includes('price') || fieldName.includes('spent') || fieldName.includes('revenue') || fieldName === 'total')) {
              value = formatCurrency(value);
            }
            return JSON.stringify(value, (_key, val) => val === null ? '' : val);
          }).join(',')
        )
      ];

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      const filename = `relatorio-${activeReport}-${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute("download", filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("Relatório completo exportado!");
    } catch (error) {
      console.error("Erro ao exportar:", error);
      toast.error("Erro ao gerar exportação");
    } finally {
      setLoading(false);
    }
  };
  const filteredData = data;

  const hasActiveFilters = startDate !== "" || endDate !== "" || includeVisitors !== false || sortBy !== "count" || searchTerm !== "" || selectedAdmin !== "" || salesStatus !== "all" || selectedSupplierId !== "";

  return (
    <main className="flex-1 p-4 md:p-6 bg-gray-50 w-full max-w-full overflow-x-auto">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#68686A]">Relatórios</h1>
          <p className="text-gray-600">Gestão e análise de dados estratégica</p>
        </div>
        <div className="flex w-full md:w-auto gap-2">
          <Button onClick={exportToCSV} variant="outline" size="sm" className="flex-1 md:flex-none flex items-center gap-2 bg-[#D4C070] text-white hover:bg-[#849D70] border-none">
            <Download className="h-4 w-4" />
            Exportar CSV
          </Button>
        </div>
      </div>

      <div className="flex flex-col xl:flex-row gap-6 w-full max-w-full min-w-0">
        {/* Sidebar e Filtros */}
        <div className="w-full xl:w-64 space-y-6 flex-shrink-0">
          <Card className="p-4 border-none shadow-sm space-y-4">
            <h3 className="font-semibold text-sm text-gray-500 uppercase tracking-wider">Filtros Globais</h3>
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-3">
              {(activeReport === 'salesman' || activeReport === 'sales') && (
                <div>
                  <label className="text-xs text-gray-400">Vendedora</label>
                  <select
                    value={selectedAdmin}
                    onChange={(e: any) => setSelectedAdmin(e.target.value)}
                    className="w-full rounded-md border-gray-200 text-sm focus:border-[#D4C070] focus:ring-[#D4C070]"
                  >
                    <option value="">Selecione...</option>
                    {admins.map(admin => (
                      <option key={admin.id} value={admin.id}>{admin.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs text-gray-400">Data Início</label>
                <Input type="date" value={startDate} onChange={(e: any) => setStartDate(e.target.value)} className="text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-400">Data Fim</label>
                <Input type="date" value={endDate} onChange={(e: any) => setEndDate(e.target.value)} className="text-sm" />
              </div>
            </div>

            {activeReport === 'abandoned' && (
              <div className="pt-2 border-t border-gray-100">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={includeVisitors}
                    onChange={(e: any) => setIncludeVisitors(e.target.checked)}
                    className="rounded border-gray-300 text-[#D4C070] focus:ring-[#D4C070]"
                  />
                  <span className="text-sm text-gray-600 group-hover:text-[#D4C070] transition-colors">Incluir sacolas de visitantes (não logados)</span>
                </label>
              </div>
            )}

            {(activeReport === 'customers' || activeReport === 'suppliers') && (
              <div className="pt-2 border-t border-gray-100">
                <label className="text-xs text-gray-400 block mb-1">Ordenar por</label>
                <select
                  value={sortBy}
                  onChange={(e: any) => setSortBy(e.target.value)}
                  className="w-full rounded-md border-gray-200 text-sm focus:border-[#D4C070] focus:ring-[#D4C070]"
                >
                  <option value="count">Qtd. de {activeReport === 'customers' ? 'Pedidos' : 'Vendas'}</option>
                  <option value="value">Valor Total {activeReport === 'customers' ? 'Gasto' : 'Vendido'}</option>
                </select>
              </div>
            )}

            {activeReport === 'sales' && (
              <div className="pt-2 border-t border-gray-100 space-y-3">
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Status do Pedido</label>
                  <select
                    value={salesStatus}
                    onChange={(e: any) => setSalesStatus(e.target.value)}
                    className="w-full rounded-md border-gray-200 text-sm focus:border-[#D4C070] focus:ring-[#D4C070]"
                  >
                    <option value="all">Todos</option>
                    <option value="sold">Aprovados</option>
                    <option value="cancelled">Cancelados</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-400 block mb-1">Fornecedor (Desapegante)</label>
                  <AsyncSelect
                    value={selectedSupplierId}
                    onChange={(id, lbl) => {
                      setSelectedSupplierId(id || "");
                      setSelectedSupplierLabel(lbl || "");
                    }}
                    fetcher={async (params) => {
                      if (!params.search || params.search.trim().length < 2) return [];
                      const res = await userService.getAll({ ...params, has_code: 1, sort: 'first_name', order: 'asc' });
                      return res.data;
                    }}
                    initialDisplay={selectedSupplierId ? selectedSupplierLabel : ""}
                    placeholder="Buscar desapegante..."
                    getOptionLabel={(u) => {
                      const fullName = [u.first_name, u.last_name].filter(Boolean).join(" ") || u.name || "";
                      return `[${u.code || u.id}] ${fullName}${u.email ? ` (${u.email})` : ""}`;
                    }}
                    renderOption={(u) => (
                      <div className="flex items-center gap-3 py-1.5 w-full">
                        <div className="flex items-center justify-center min-w-[55px] bg-gray-800 text-white text-[10px] font-bold px-2 py-1 rounded border border-gray-700 uppercase tracking-tighter shadow-sm">
                          {u.code || u.id}
                        </div>
                        <div className="flex flex-col text-left">
                          <span className="text-sm font-semibold text-gray-800">
                            {[u.first_name, u.last_name].filter(Boolean).join(" ") || u.name || "N/A"}
                          </span>
                          {u.email && <span className="text-xs text-gray-400">{u.email}</span>}
                        </div>
                      </div>
                    )}
                  />
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-gray-100">
              <Button
                onClick={clearFilters}
                variant="ghost"
                size="sm"
                disabled={!hasActiveFilters}
                className={`w-full flex items-center justify-center gap-2 transition-all ${hasActiveFilters
                  ? "text-red-500 hover:bg-red-50"
                  : "text-gray-300 cursor-not-allowed"
                  }`}
              >
                <Filter className="h-3 w-3" />
                Limpar Filtros
              </Button>
            </div>
          </Card>

          <div className="flex overflow-x-auto lg:flex-col gap-2 pb-2 lg:pb-0 no-scrollbar">
            {reports.map((report) => (
              <button
                key={report.id}
                onClick={() => {
                  setActiveReport(report.id);
                  setSearchTerm("");
                }}
                className={`flex-shrink-0 lg:flex-shrink flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${activeReport === report.id
                  ? "bg-[#D4C070] text-white shadow-md shadow-[#D4C070]/20"
                  : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                  }`}
              >
                <report.icon className="h-4 w-4" />
                <span className="whitespace-nowrap">{report.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Conteúdo do Relatório */}
        <div className="flex-1 min-w-0 w-full max-w-full">
          <Card className="border-none shadow-sm min-h-[500px] w-full max-w-full overflow-hidden">
            <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pb-2">
              <CardTitle className="text-lg font-semibold text-[#68686A]">
                {reports.find(r => r.id === activeReport)?.name}
                {totalItems > 0 && <span className="ml-2 text-xs font-normal text-gray-400">({totalItems} registros)</span>}
              </CardTitle>
              <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={(e: any) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </CardHeader>
            <CardContent className="w-full max-w-full p-4 md:p-6">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <Loader2 className="h-10 w-10 animate-spin text-[#D4C070] mb-4" />
                  <p className="text-gray-500 font-medium">Processando dados...</p>
                </div>
              ) : filteredData.length === 0 ? (
                <div className="text-center py-20 border-2 border-dashed border-gray-100 rounded-lg">
                  <FileText className="h-12 w-12 text-gray-200 mx-auto mb-4" />
                  <p className="text-gray-500">Nenhum dado encontrado para os filtros aplicados.</p>
                </div>
              ) : (
                <>
                  {/* {activeReport === 'states' && (
                    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                      <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                        <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">Preço Líquido</p>
                        <p className="text-sm font-bold text-gray-700">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(filteredData.reduce((acc, curr) => acc + (curr.total_net || 0), 0))}
                        </p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                        <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">Total Frete</p>
                        <p className="text-sm font-bold text-gray-700">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(filteredData.reduce((acc, curr) => acc + (curr.total_shipping || 0), 0))}
                        </p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                        <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">Total Juros</p>
                        <p className="text-sm font-bold text-gray-700">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(filteredData.reduce((acc, curr) => acc + (curr.total_juros || 0), 0))}
                        </p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                        <p className="text-[10px] text-gray-400 uppercase font-bold mb-1">Certificados</p>
                        <p className="text-sm font-bold text-gray-700">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(filteredData.reduce((acc, curr) => acc + (curr.total_certificate || 0), 0))}
                        </p>
                      </div>
                      <div className="bg-[#D4C070]/10 p-3 rounded-lg border border-[#D4C070]/20">
                        <p className="text-[10px] text-[#D4C070] uppercase font-bold mb-1">Faturamento Bruto</p>
                        <p className="text-sm font-bold text-[#D4C070]">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(filteredData.reduce((acc, curr) => acc + (curr.total || 0), 0))}
                        </p>
                      </div>
                    </div>
                  )} */}

                  {activeReport === 'category_pieces' && (
                    <div className="grid grid-cols-1 gap-4 mb-6 w-full">
                      <div className="bg-amber-50 p-4 rounded-xl border border-amber-100 min-w-0 shadow-sm flex items-center justify-between">
                        <div>
                          <p className="text-xs text-amber-700 uppercase font-bold mb-1">Peças em Estoque (Disponíveis Hoje)</p>
                          <p className="text-2xl sm:text-3xl font-black text-amber-800" title={String(totalAvailable)}>
                            {totalAvailable} peças
                          </p>
                        </div>
                        <Package className="h-8 w-8 text-amber-500 opacity-60" />
                      </div>
                    </div>
                  )}

                  {(activeReport === 'salesman' || activeReport === 'sales' || (totalRevenue > 0 || totalNetRevenue > 0)) && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 w-full">
                      <div className="bg-[#D4C070]/10 p-4 rounded-xl border border-[#D4C070]/20 min-w-0 shadow-sm">
                        <p className="text-xs text-[#D4C070] uppercase font-bold mb-1 truncate">Bruto no Período</p>
                        <p
                          className="text-lg sm:text-xl xl:text-2xl font-black text-[#D4C070] truncate"
                          title={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalRevenue)}
                        >
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalRevenue)}
                        </p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 min-w-0 shadow-sm">
                        <p className="text-xs text-gray-400 uppercase font-bold mb-1 truncate">Líquido no Período</p>
                        <p
                          className="text-lg sm:text-xl xl:text-2xl font-black text-gray-700 truncate"
                          title={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalNetRevenue)}
                        >
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(totalNetRevenue)}
                        </p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 min-w-0 shadow-sm">
                        <p className="text-xs text-gray-400 uppercase font-bold mb-1 truncate">
                          {activeReport === 'salesman' ? 'Total de Peças' : 'Total de Registros'}
                        </p>
                        <p className="text-lg sm:text-xl xl:text-2xl font-black text-gray-700 truncate" title={String(totalItems)}>
                          {totalItems}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Vista Mobile (Cards) */}
                  <div className="grid grid-cols-1 gap-4 md:hidden w-full max-w-full min-w-0">
                    {filteredData.map((item: any, idx: number) => (
                      <div key={idx} className="bg-white border border-gray-100 rounded-lg p-4 shadow-sm min-w-0 max-w-full overflow-hidden">
                        {activeReport === 'category_pieces' && (
                          <div className="space-y-3">
                            <div
                              className="flex justify-between items-center cursor-pointer select-none"
                              onClick={() => toggleCategoryAccordion(item.id)}
                            >
                              <div className="flex items-center gap-2">
                                {item.subcategories && item.subcategories.length > 0 ? (
                                  expandedCategories.includes(item.id) ? (
                                    <ChevronDown className="h-4 w-4 text-[#D4C070]" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 text-gray-400" />
                                  )
                                ) : (
                                  <span className="w-4 inline-block" />
                                )}
                                <span className="font-bold text-gray-900 text-base">{item.category_name}</span>
                              </div>
                              {item.subcategories && item.subcategories.length > 0 && (
                                <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-normal">
                                  {item.subcategories.length} subcategoria{item.subcategories.length > 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                            <div className="flex justify-between items-center text-xs pt-2 border-t border-gray-50 font-bold uppercase">
                              <span className="text-gray-500 font-normal">Estoque Disponível</span>
                              <span className="bg-amber-50 text-amber-800 px-2 py-1 rounded border border-amber-100 font-black">{item.total_available}</span>
                            </div>
                            {item.subcategories && item.subcategories.length > 0 && expandedCategories.includes(item.id) && (
                              <div className="mt-3 pt-3 border-t border-gray-100 space-y-2 pl-2 border-l-2 border-l-[#D4C070]">
                                <p className="text-[10px] font-bold uppercase text-gray-400">Subcategorias:</p>
                                {item.subcategories.map((sub: any, sIdx: number) => (
                                  <div key={sIdx} className="flex justify-between items-center text-xs bg-gray-50 p-2 rounded">
                                    <span className="font-medium text-gray-800">• {sub.category_name}</span>
                                    <span className="text-amber-700 font-bold">{sub.total_available} estoque</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        {activeReport === 'sales' && salesView === 'consolidated' && (
                          <div className="space-y-3">
                            <div className="flex justify-between items-start">
                              <div className="font-bold text-[#68686A]">#{item.order_id}</div>
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${item.status === 'Aprovado' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                {item.status}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500">Cliente: <span className="font-bold">{decodeHtmlEntities(item.customer_name)}</span> ({item.uf})</div>
                            <div className="text-xs text-gray-500">Gateway: <span className="font-semibold">{item.gateway}</span></div>
                            <div className="flex justify-between items-center pt-2 border-t border-gray-50">
                              <span className="text-xs text-gray-400">Efetuado em: {item.effective_date}</span>
                              <div className="text-right">
                                <div className="text-[10px] text-gray-400">Líquido Prod: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.net_products)}</div>
                                <div className="font-bold text-[#D4C070]">
                                  Total: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.total)}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {activeReport === 'sales' && (
                          <div className="space-y-3">
                            <div className="flex justify-between items-start">
                              <div className="font-bold text-gray-900">{decodeHtmlEntities(item.product_name)}</div>
                              <div className="text-[10px] font-bold text-gray-400">Pedido #{item.order_id}</div>
                            </div>
                            <div className="text-xs text-gray-500">
                              Cliente: {item.customer_code && <span className="font-bold text-gray-800 mr-1">[{item.customer_code}]</span>}
                              <span className="font-medium">{decodeHtmlEntities(item.customer_name)}</span>
                              {item.customer_email && item.customer_email !== 'N/A' && <span className="block text-[10px] text-gray-400">{item.customer_email}</span>}
                            </div>
                            <div className="text-xs text-gray-500">Marca: <span className="font-medium">{item.brand || 'N/A'}</span></div>
                            <div className="text-xs text-gray-500">Fornecedor: <span className="font-medium">{decodeHtmlEntities(item.supplier)}</span></div>
                            <div className="text-xs text-gray-500">Vendedor: <span className="font-medium">{decodeHtmlEntities(item.salesman)}</span></div>
                            <div className="text-xs text-gray-500">Pagamento: <span className="font-semibold">{item.parceled || 'N/A'}</span></div>
                            <div className="flex justify-between items-center pt-2 border-t border-gray-50">
                              <div className="flex flex-col text-[10px] text-gray-400">
                                <span>Criado: {item.created_at}</span>
                                <span>Efetivado: {item.effective_date}</span>
                              </div>
                              <div className="text-right">
                                <div className="text-[10px] text-gray-400">{item.quantity}x {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.price)}</div>
                                <div className="text-[10px] text-gray-400">Frete: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.shipping_price || 0)}</div>
                                <div className="font-bold text-[#D4C070]">
                                  Total: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.total)}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {activeReport === 'salesman' && (
                          <div className="space-y-3">
                            <div className="flex justify-between items-start">
                              <div className="font-bold text-[#68686A]">{decodeHtmlEntities(item.product_name)}</div>
                              <div className="text-[10px] font-bold text-gray-400">#{item.order_id}</div>
                            </div>
                            <div className="text-xs text-gray-500">Cliente: <span className="font-bold">{decodeHtmlEntities(item.customer)}</span></div>
                            <div className="text-xs text-gray-500">Pagamento: <span className="font-semibold">{item.gateway || 'N/A'}</span></div>
                            <div className="flex justify-between items-center pt-2 border-t border-gray-50">
                              <span className="text-xs text-gray-400">{item.sold_at}</span>
                              <div className="text-right">
                                <div className="text-[10px] text-gray-400">Líquido: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.net_price)}</div>
                                <div className="font-bold text-[#D4C070]">
                                  Bruto: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.price)}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {activeReport === 'abandoned' && (
                          <div className="space-y-3">
                            <div className="font-bold text-gray-900">{decodeHtmlEntities(item.user_name)}</div>
                            <div className="space-y-1">
                              <button
                                onClick={() => copyToClipboard(item.user_email, "Email")}
                                className="text-xs text-gray-500 flex items-center gap-1 hover:text-[#D4C070] cursor-pointer"
                              >
                                <Mail className="h-3 w-3" /> {item.user_email}
                              </button>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => copyToClipboard(item.user_phone, "Telefone")}
                                  className="text-xs text-gray-500 flex items-center gap-1 hover:text-[#D4C070] cursor-pointer"
                                >
                                  <Copy className="h-3 w-3" /> {item.user_phone}
                                </button>
                                <button
                                  onClick={() => openWhatsApp(item.user_phone)}
                                  className="text-[10px] bg-green-50 text-green-600 px-2 py-0.5 rounded-full flex items-center gap-1 font-bold cursor-pointer"
                                >
                                  <MessageCircle className="h-3 w-3" /> WhatsApp
                                </button>
                              </div>
                            </div>
                            <div className="text-sm text-gray-600 flex items-center gap-2 pt-1 border-t border-gray-50">
                              {decodeHtmlEntities(item.product_name)}
                              <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase ${item.product_status === 'Vendido' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                                }`}>{item.product_status}</span>
                            </div>
                            <div className="flex justify-between text-xs text-gray-400">
                              <span>{item.added_at}</span>
                              <span className="font-bold text-[#D4C070]">
                                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.product_price)}
                              </span>
                            </div>
                          </div>
                        )}
                        {activeReport === 'customers' && (
                          <div className="space-y-3">
                            <div className="font-bold text-gray-900">{decodeHtmlEntities(item.name)}</div>
                            <div className="space-y-1">
                              <button
                                onClick={() => copyToClipboard(item.email, "Email")}
                                className="text-xs text-gray-500 flex items-center gap-1 hover:text-[#D4C070] cursor-pointer"
                              >
                                <Mail className="h-3 w-3" /> {item.email}
                              </button>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => copyToClipboard(item.phone, "Telefone")}
                                  className="text-xs text-gray-500 flex items-center gap-1 hover:text-[#D4C070] cursor-pointer"
                                >
                                  <Copy className="h-3 w-3" /> {item.phone}
                                </button>
                                <button
                                  onClick={() => openWhatsApp(item.phone)}
                                  className="text-[10px] bg-green-50 text-green-600 px-2 py-0.5 rounded-full flex items-center gap-1 font-bold cursor-pointer"
                                >
                                  <MessageCircle className="h-3 w-3" /> WhatsApp
                                </button>
                              </div>
                            </div>
                            <div className="flex justify-between text-sm pt-2 border-t border-gray-50">
                              <span>{item.orders_count} pedidos</span>
                              <div className="text-right">
                                <div className="text-[10px] text-gray-400">Líquido: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.total_net_spent)}</div>
                                <div className="font-bold text-[#D4C070]">
                                  Bruto: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.total_spent)}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {activeReport === 'suppliers' && (
                          <div className="space-y-3">
                            <div className="font-bold text-gray-900">{decodeHtmlEntities(item.name)}</div>
                            <div className="space-y-1">
                              <button
                                onClick={() => copyToClipboard(item.email, "Email")}
                                className="text-xs text-gray-500 flex items-center gap-1 hover:text-[#D4C070] cursor-pointer"
                              >
                                <Mail className="h-3 w-3" /> {item.email}
                              </button>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => copyToClipboard(item.phone, "Telefone")}
                                  className="text-xs text-gray-500 flex items-center gap-1 hover:text-[#D4C070] cursor-pointer"
                                >
                                  <Copy className="h-3 w-3" /> {item.phone}
                                </button>
                                <button
                                  onClick={() => openWhatsApp(item.phone)}
                                  className="text-[10px] bg-green-50 text-green-600 px-2 py-0.5 rounded-full flex items-center gap-1 font-bold cursor-pointer"
                                >
                                  <MessageCircle className="h-3 w-3" /> WhatsApp
                                </button>
                              </div>
                            </div>
                            <div className="flex justify-between text-sm pt-2 border-t border-gray-50">
                              <span>{item.sold_count} vendidos</span>
                              <div className="text-right">
                                <div className="text-[10px] text-gray-400">Líquido: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.total_net_revenue)}</div>
                                <div className="font-bold text-[#D4C070]">
                                  Bruto: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.total_revenue)}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {activeReport === 'states' && (
                          <div className="space-y-2">
                            <div className="flex justify-between font-bold">
                              <span className="uppercase">{item.uf}</span>
                              <span className="text-[#D4C070]">
                                {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.total)}
                              </span>
                            </div>
                            <div className="text-xs text-gray-400">{item.count} vendas</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Vista Desktop (Tabela) */}
                  <div className="hidden md:block w-full max-w-full overflow-x-auto min-w-0 rounded-lg border border-gray-100/50">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr className="border-b border-gray-100 text-left text-xs font-bold text-[#68686A] bg-gray-50/50 uppercase tracking-wider">
                          {activeReport === 'sales' && salesView === 'consolidated' && (
                            <>
                              <th className="px-4 py-4">Pedido</th>
                              <th className="px-4 py-4">Data Pedido</th>
                              <th className="px-4 py-4">Data Efetivação</th>
                              <th className="px-4 py-4">Cliente</th>
                              <th className="px-4 py-4 text-center">UF</th>
                              <th className="px-4 py-4">Status</th>
                              <th className="px-4 py-4">Gateway</th>
                              <th className="px-4 py-4 text-right">Cupom</th>
                              <th className="px-4 py-4 text-right">Frete</th>
                              <th className="px-4 py-4 text-right">Líq. Prod.</th>
                              <th className="px-4 py-4 text-right">Juros</th>
                              <th className="px-4 py-4 text-right">Cert.</th>
                              <th className="px-4 py-4 text-right">Total Pago</th>
                            </>
                          )}
                          {activeReport === 'sales' && salesView === 'detailed' && (
                            <>
                              <th className="px-4 py-4">Pedido</th>
                              <th className="px-4 py-4">Criação</th>
                              <th className="px-4 py-4">Efetivação</th>
                              <th className="px-4 py-4">Cliente</th>
                              <th className="px-4 py-4">SKU</th>
                              <th className="px-4 py-4">Produto</th>
                              <th className="px-4 py-4">Marca</th>
                              <th className="px-4 py-4">Fornecedor</th>
                              <th className="px-4 py-4">Status</th>
                              <th className="px-4 py-4 text-right">Unitário</th>
                              <th className="px-4 py-4 text-center">Qtd</th>
                              <th className="px-4 py-4 text-right">Frete</th>
                              <th className="px-4 py-4 text-center">Pagamento</th>
                              <th className="px-4 py-4 text-right">Total Líquido</th>
                              <th className="px-4 py-4">Vendedor</th>
                            </>
                          )}
                          {activeReport === 'abandoned' && (
                            <>
                              <th className="px-4 py-4">Cliente / Contato</th>
                              <th className="px-4 py-4">Produto</th>
                              <th className="px-4 py-4 text-right">Preço</th>
                              <th className="px-4 py-4">Adicionado em</th>
                            </>
                          )}
                          {activeReport === 'customers' && (
                            <>
                              <th className="px-4 py-4">Cliente / Contato</th>
                              <th className="px-4 py-4 text-center">Pedidos</th>
                              <th className="px-4 py-4 text-right">Líquido</th>
                              <th className="px-4 py-4 text-right">Total Bruto</th>
                              <th className="px-4 py-4">Membro desde</th>
                            </>
                          )}
                          {activeReport === 'suppliers' && (
                            <>
                              <th className="px-4 py-4">Desapegante / Contato</th>
                              <th className="px-4 py-4 text-center">Itens Vendidos</th>
                              <th className="px-4 py-4 text-right">Líquido</th>
                              <th className="px-4 py-4 text-right">Valor Bruto</th>
                            </>
                          )}
                          {activeReport === 'states' && (
                            <>
                              <th className="px-4 py-4">Estado (UF)</th>
                              <th className="px-4 py-4 text-center">Nº Vendas</th>
                              <th className="px-4 py-4 text-right">Líquido</th>
                              <th className="px-4 py-4 text-right">Frete</th>
                              <th className="px-4 py-4 text-right">Juros</th>
                              <th className="px-4 py-4 text-right">Cert.</th>
                              <th className="px-4 py-4 text-right">Total Bruto</th>
                              <th className="px-4 py-4">Part.</th>
                            </>
                          )}
                          {activeReport === 'category_pieces' && (
                            <>
                              <th className="px-4 py-4">Categoria / Subcategoria</th>
                              <th className="px-4 py-4 text-right">Peças em Estoque</th>
                            </>
                          )}
                          {activeReport === 'salesman' && (
                            <>
                              <th className="px-4 py-4">Pedido</th>
                              <th className="px-4 py-4">Produto</th>
                              <th className="px-4 py-4 text-right">Líquido</th>
                              <th className="px-4 py-4 text-right">Valor Bruto</th>
                              <th className="px-4 py-4">Pagamento</th>
                              <th className="px-4 py-4">Cliente</th>
                              <th className="px-4 py-4">Data da Venda</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {filteredData.map((item: any, idx: number) => (
                          <React.Fragment key={idx}>
                            <tr className="hover:bg-gray-50/50 transition-colors text-sm">
                              {activeReport === 'sales' && salesView === 'consolidated' && (
                                <>
                                  <td className="px-4 py-4 font-bold text-gray-400">#{item.order_id}</td>
                                  <td className="px-4 py-4 text-gray-500">{item.created_at}</td>
                                  <td className="px-4 py-4 text-gray-500 font-medium">{item.effective_date}</td>
                                  <td className="px-4 py-4 text-gray-900 font-semibold" title={item.customer_name}>
                                    <div>{decodeHtmlEntities(item.customer_name)}</div>
                                    <div className="text-[10px] text-gray-400 flex flex-col">
                                      <span>{item.customer_email}</span>
                                      <span>{item.customer_phone}</span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 text-center uppercase font-bold text-gray-600">{item.uf}</td>
                                  <td className="px-4 py-4">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${item.status === 'Aprovado' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                      {item.status}
                                    </span>
                                  </td>
                                  <td className="px-4 py-4 text-gray-600 font-medium">{item.gateway}</td>
                                  <td className="px-4 py-4 text-right text-xs text-gray-500">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.discount || 0)}
                                  </td>
                                  <td className="px-4 py-4 text-right text-xs text-gray-500">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.shipping || 0)}
                                  </td>
                                  <td className="px-4 py-4 text-right text-xs font-semibold text-gray-600">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.net_products || 0)}
                                  </td>
                                  <td className="px-4 py-4 text-right text-xs text-gray-500">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.juros || 0)}
                                  </td>
                                  <td className="px-4 py-4 text-right text-xs text-gray-500">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.certificate || 0)}
                                  </td>
                                  <td className="px-4 py-4 text-right font-bold text-[#D4C070]">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.total || 0)}
                                  </td>
                                </>
                              )}
                              {activeReport === 'sales' && salesView === 'detailed' && (
                                <>
                                  <td className="px-4 py-4 font-bold text-gray-400">#{item.order_id}</td>
                                  <td className="px-4 py-4 text-gray-500 text-xs">{item.created_at}</td>
                                  <td className="px-4 py-4 text-gray-500 text-xs">{item.effective_date}</td>
                                  <td className="px-4 py-4 text-gray-900 font-semibold">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      {item.customer_code && (
                                        <span className="inline-block bg-gray-800 text-white text-[10px] font-bold px-1.5 py-0.5 rounded border border-gray-700 uppercase tracking-tighter shadow-sm">
                                          {item.customer_code}
                                        </span>
                                      )}
                                      <span>{decodeHtmlEntities(item.customer_name)}</span>
                                    </div>
                                    <div className="text-[10px] text-gray-400 flex flex-col font-normal mt-0.5">
                                      {item.customer_email && item.customer_email !== 'N/A' && <span>{item.customer_email}</span>}
                                      {item.customer_phone && item.customer_phone !== 'N/A' && <span>{item.customer_phone}</span>}
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 text-xs font-bold text-gray-500">{item.sku}</td>
                                  <td className="px-4 py-4 font-medium text-[#68686A]" title={item.product_name}>
                                    {decodeHtmlEntities(item.product_name)}
                                  </td>
                                  <td className="px-4 py-4 text-gray-500">{item.brand}</td>
                                  <td className="px-4 py-4 font-medium text-gray-600" title={item.supplier}>
                                    {decodeHtmlEntities(item.supplier)}
                                  </td>
                                  <td className="px-4 py-4">
                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${item.status === 'Aprovado' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                      {item.status}
                                    </span>
                                  </td>
                                  <td className="px-4 py-4 text-right text-xs text-gray-500">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.price || 0)}
                                  </td>
                                  <td className="px-4 py-4 text-center font-medium">{item.quantity}</td>
                                  <td className="px-4 py-4 text-right text-xs text-gray-500">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.shipping_price || 0)}
                                  </td>
                                  <td className="px-4 py-4 text-center text-xs text-gray-500 font-medium">{item.parceled || 'N/A'}</td>
                                  <td className="px-4 py-4 text-right font-bold text-[#D4C070]">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.total || 0)}
                                  </td>
                                  <td className="px-4 py-4 text-gray-500">{decodeHtmlEntities(item.salesman)}</td>
                                </>
                              )}
                              {activeReport === 'category_pieces' && (
                                <>
                                  <td className="px-4 py-4">
                                    <div className="flex items-center gap-2">
                                      {item.subcategories && item.subcategories.length > 0 ? (
                                        <button
                                          onClick={() => toggleCategoryAccordion(item.id)}
                                          className="p-1 hover:bg-gray-100 rounded-md transition-colors text-gray-500 cursor-pointer"
                                        >
                                          {expandedCategories.includes(item.id) ? (
                                            <ChevronDown className="h-4 w-4 text-[#D4C070]" />
                                          ) : (
                                            <ChevronRight className="h-4 w-4 text-gray-400" />
                                          )}
                                        </button>
                                      ) : (
                                        <span className="w-6 inline-block" />
                                      )}
                                      <span className="font-bold text-gray-900 text-base">{item.category_name}</span>
                                      {item.subcategories && item.subcategories.length > 0 && (
                                        <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded font-normal">
                                          {item.subcategories.length} subcategoria{item.subcategories.length > 1 ? 's' : ''}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 text-right font-bold text-amber-700 text-base">{item.total_available}</td>
                                </>
                              )}
                              {activeReport === 'salesman' && (
                                <>
                                  <td className="px-4 py-4 font-bold text-gray-400">#{item.order_id}</td>
                                  <td className="px-4 py-4">
                                    <div className="font-medium text-[#68686A]">{decodeHtmlEntities(item.product_name)}</div>
                                    <div className="text-[10px] text-gray-400">{item.product_sku}</div>
                                  </td>
                                  <td className="px-4 py-4 text-right text-xs text-gray-500">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.net_price)}
                                  </td>
                                  <td className="px-4 py-4 text-right font-bold text-[#D4C070]">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.price)}
                                  </td>
                                  <td className="px-4 py-4 text-gray-600 font-medium">{item.gateway || 'N/A'}</td>
                                  <td className="px-4 py-4 text-gray-600 font-medium">{decodeHtmlEntities(item.customer)}</td>
                                  <td className="px-4 py-4 text-gray-500">{item.sold_at}</td>
                                </>
                              )}
                              {activeReport === 'abandoned' && (
                                <>
                                  <td className="px-4 py-4">
                                    <div className="font-semibold text-gray-900 mb-1">{decodeHtmlEntities(item.user_name)}</div>
                                    <div className="space-y-1">
                                      <button
                                        onClick={() => copyToClipboard(item.user_email, "Email")}
                                        className="text-[10px] text-gray-400 flex items-center gap-1 hover:text-[#D4C070] transition-colors truncate w-full cursor-pointer"
                                        title="Clique para copiar email"
                                      >
                                        <Mail className="h-2.5 w-2.5" /> {item.user_email}
                                      </button>
                                      <div className="flex items-center gap-2">
                                        <button
                                          onClick={() => copyToClipboard(item.user_phone, "Telefone")}
                                          className="text-[10px] text-gray-400 flex items-center gap-1 hover:text-[#D4C070] transition-colors cursor-pointer"
                                          title="Clique para copiar telefone"
                                        >
                                          <Copy className="h-2.5 w-2.5" /> {item.user_phone}
                                        </button>
                                        <button
                                          onClick={() => openWhatsApp(item.user_phone)}
                                          className="text-[9px] bg-green-50 text-green-600 px-1.5 py-0.5 rounded-full flex items-center gap-1 font-bold hover:bg-green-100 transition-colors cursor-pointer"
                                        >
                                          <MessageCircle className="h-2.5 w-2.5" /> WA
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-4">
                                    <div className="flex items-center gap-2">
                                      <div className="font-medium text-[#68686A]">{decodeHtmlEntities(item.product_name)}</div>
                                      <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase ${item.product_status === 'Vendido' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                                        }`}>{item.product_status}</span>
                                    </div>
                                    <div className="text-xs text-gray-400">{item.product_sku}</div>
                                  </td>
                                  <td className="px-4 py-4 text-right font-bold text-[#D4C070]">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.product_price)}
                                  </td>
                                  <td className="px-4 py-4 text-gray-500">{item.added_at}</td>
                                </>
                              )}
                              {activeReport === 'customers' && (
                                <>
                                  <td className="px-4 py-4">
                                    <div className="font-bold text-gray-900 mb-1">{decodeHtmlEntities(item.name)}</div>
                                    <div className="space-y-1">
                                      <button
                                        onClick={() => copyToClipboard(item.email, "Email")}
                                        className="text-[10px] text-gray-400 flex items-center gap-1 hover:text-[#D4C070] transition-colors truncate w-full cursor-pointer"
                                        title="Clique para copiar email"
                                      >
                                        <Mail className="h-2.5 w-2.5" /> {item.email}
                                      </button>
                                      <div className="flex items-center gap-2">
                                        <button
                                          onClick={() => copyToClipboard(item.phone, "Telefone")}
                                          className="text-[10px] text-gray-400 flex items-center gap-1 hover:text-[#D4C070] transition-colors cursor-pointer"
                                          title="Clique para copiar telefone"
                                        >
                                          <Copy className="h-2.5 w-2.5" /> {item.phone}
                                        </button>
                                        <button
                                          onClick={() => openWhatsApp(item.phone)}
                                          className="text-[9px] bg-green-50 text-green-600 px-1.5 py-0.5 rounded-full flex items-center gap-1 font-bold hover:bg-green-100 transition-colors cursor-pointer"
                                        >
                                          <MessageCircle className="h-2.5 w-2.5" /> WA
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 text-center font-medium">{item.orders_count}</td>
                                  <td className="px-4 py-4 text-right text-xs text-gray-500">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.total_net_spent)}
                                  </td>
                                  <td className="px-4 py-4 text-right font-bold text-[#D4C070]">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.total_spent)}
                                  </td>
                                  <td className="px-4 py-4 text-gray-500">{item.joined_at}</td>
                                </>
                              )}
                              {activeReport === 'suppliers' && (
                                <>
                                  <td className="px-4 py-4">
                                    <div className="font-bold text-gray-900 mb-1">{decodeHtmlEntities(item.name)}</div>
                                    <div className="space-y-1">
                                      <button
                                        onClick={() => copyToClipboard(item.email, "Email")}
                                        className="text-[10px] text-gray-400 flex items-center gap-1 hover:text-[#D4C070] transition-colors truncate w-full cursor-pointer"
                                        title="Clique para copiar email"
                                      >
                                        <Mail className="h-2.5 w-2.5" /> {item.email}
                                      </button>
                                      <div className="flex items-center gap-2">
                                        <button
                                          onClick={() => copyToClipboard(item.phone, "Telefone")}
                                          className="text-[10px] text-gray-400 flex items-center gap-1 hover:text-[#D4C070] transition-colors cursor-pointer"
                                          title="Clique para copiar telefone"
                                        >
                                          <Copy className="h-2.5 w-2.5" /> {item.phone}
                                        </button>
                                        <button
                                          onClick={() => openWhatsApp(item.phone)}
                                          className="text-[9px] bg-green-50 text-green-600 px-1.5 py-0.5 rounded-full flex items-center gap-1 font-bold hover:bg-green-100 transition-colors cursor-pointer"
                                        >
                                          <MessageCircle className="h-2.5 w-2.5" /> WA
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 text-center font-medium">{item.sold_count}</td>
                                  <td className="px-4 py-4 text-right text-xs text-gray-500">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.total_net_revenue)}
                                  </td>
                                  <td className="px-4 py-4 text-right font-bold text-[#D4C070]">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.total_revenue)}
                                  </td>
                                </>
                              )}
                              {activeReport === 'states' && (
                                <>
                                  <td className="px-4 py-4">
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => toggleState(item.uf)}
                                        className="p-1 hover:bg-gray-100 rounded-md transition-colors text-gray-400"
                                      >
                                        {expandedStates.includes(item.uf) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                      </button>
                                      <span className="font-bold text-gray-900 uppercase">{item.uf}</span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-4 text-center font-medium">{item.count}</td>
                                  <td className="px-4 py-4 text-right text-xs text-gray-500">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.total_net)}
                                  </td>
                                  <td className="px-4 py-4 text-right text-xs text-gray-500">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.total_shipping)}
                                  </td>
                                  <td className="px-4 py-4 text-right text-xs text-gray-500">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.total_juros)}
                                  </td>
                                  <td className="px-4 py-4 text-right text-xs text-gray-500">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.total_certificate)}
                                  </td>
                                  <td className="px-4 py-4 text-right font-bold text-[#D4C070]">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(item.total)}
                                  </td>
                                  <td className="px-4 py-4 w-24">
                                    <div className="w-full bg-gray-100 rounded-full h-1.5">
                                      <div
                                        className="bg-[#D4C070] h-1.5 rounded-full"
                                        style={{ width: `${Math.min(100, (item.total / (filteredData[0]?.total || 1)) * 100)}%` }}
                                      />
                                    </div>
                                  </td>
                                </>
                              )}
                            </tr>
                            {activeReport === 'category_pieces' && item.subcategories && item.subcategories.length > 0 && expandedCategories.includes(item.id) && (
                              item.subcategories.map((sub: any, sIdx: number) => (
                                <tr key={`sub-${sub.id}-${sIdx}`} className="bg-gray-50/50 border-l-4 border-l-[#D4C070] text-xs hover:bg-gray-100/50 transition-colors">
                                  <td className="px-4 py-3 pl-12 font-medium text-gray-700">
                                    • {sub.category_name}
                                  </td>
                                  <td className="px-4 py-3 text-right font-semibold text-amber-600">{sub.total_available}</td>
                                </tr>
                              ))
                            )}
                            {activeReport === 'states' && expandedStates.includes(item.uf) && item.cities?.map((city: any, cIdx: number) => (
                              <React.Fragment key={`city-${cIdx}`}>
                                <tr className="bg-gray-50/30 border-l-4 border-l-[#D4C070]/30">
                                  <td className="px-4 py-3 pl-12">
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => toggleCity(`${item.uf}-${city.name}`)}
                                        className="p-1 hover:bg-gray-100 rounded-md transition-colors text-gray-400"
                                      >
                                        {expandedCities.includes(`${item.uf}-${city.name}`) ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                      </button>
                                      <span className="text-sm font-medium text-gray-700">{city.name}</span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-center text-xs text-gray-500 font-medium">{city.count}</td>
                                  <td className="px-4 py-3 text-right text-xs text-gray-500">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(city.total_net)}</td>
                                  <td className="px-4 py-3 text-right text-xs text-gray-500">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(city.total_shipping)}</td>
                                  <td className="px-4 py-3 text-right text-xs text-gray-500">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(city.total_juros)}</td>
                                  <td className="px-4 py-3 text-right text-xs text-gray-500">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(city.total_certificate)}</td>
                                  <td className="px-4 py-3 text-right text-sm font-bold text-gray-700">
                                    {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(city.total)}
                                  </td>
                                  <td></td>
                                </tr>
                                {expandedCities.includes(`${item.uf}-${city.name}`) && city.neighborhoods?.map((nb: any, nIdx: number) => (
                                  <tr key={`nb-${nIdx}`} className="bg-white border-l-4 border-l-[#D4C070]/10 italic">
                                    <td className="px-4 py-2 pl-20 text-xs text-gray-400">
                                      • {nb.name}
                                    </td>
                                    <td className="px-4 py-2 text-center text-[11px] text-gray-500">{nb.count}</td>
                                    <td className="px-4 py-2 text-right text-[11px] text-gray-500">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(nb.total_net)}</td>
                                    <td className="px-4 py-2 text-right text-[11px] text-gray-500">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(nb.total_shipping)}</td>
                                    <td className="px-4 py-2 text-right text-[11px] text-gray-500">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(nb.total_juros)}</td>
                                    <td className="px-4 py-2 text-right text-[11px] text-gray-500">{new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(nb.total_certificate)}</td>
                                    <td className="px-4 py-2 text-right text-xs font-medium text-gray-600">
                                      {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(nb.total)}
                                    </td>
                                    <td></td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            ))}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Controles de Paginação */}
                  {lastPage > 1 && (
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-6 pt-6 border-t border-gray-100">
                      <p className="text-sm text-gray-500">
                        Mostrando página <span className="font-bold text-[#68686A]">{currentPage}</span> de <span className="font-bold text-[#68686A]">{lastPage}</span>
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => fetchData(currentPage - 1)}
                          disabled={currentPage === 1}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-1"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Anterior
                        </Button>
                        <div className="flex items-center gap-1">
                          {[...Array(Math.min(5, lastPage))].map((_: any, i: number) => {
                            let pageNum = currentPage <= 3 ? i + 1 : currentPage + i - 2;
                            if (pageNum > lastPage) pageNum = lastPage - (4 - i);
                            if (pageNum < 1) return null;

                            return (
                              <button
                                key={i}
                                onClick={() => fetchData(pageNum)}
                                className={`w-8 h-8 rounded-md text-xs font-bold transition-all ${currentPage === pageNum
                                  ? "bg-[#D4C070] text-white shadow-sm"
                                  : "bg-white text-gray-400 hover:bg-gray-100 border border-gray-200"
                                  }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>
                        <Button
                          onClick={() => fetchData(currentPage + 1)}
                          disabled={currentPage === lastPage}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-1"
                        >
                          Próxima
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </main>
  );
}

export default RelatoriosPage;
